import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoIcon } from './PlatformIcons.tsx';

/**
 * L'aiuto di un campo, dietro un'icona — ma sempre nell'albero di
 * accessibilità.
 *
 * Diverso da `InfoTip.tsx` apposta, non una variante: quello usa
 * `<details>`, che tiene il contenuto **fuori** dall'albero di accessibilità
 * finché non si apre — bene per la spiegazione di una scheda, sbagliato per
 * l'aiuto di un campo, che deve restare raggiungibile da `aria-describedby`
 * indipendentemente da cosa vede chi guarda lo schermo (sezione 7bis): un
 * `<details>` chiuso toglierebbe l'aiuto a chi usa uno screen reader proprio
 * arrivando sul campo, il momento in cui serve di più.
 *
 * Qui il testo resta **sempre montato** e sempre puntato dall'
 * `aria-describedby` scritto a mano sull'`<input>`/`<select>` che questo
 * componente descrive — cambia solo la visibilità *visiva*, con
 * `.visually-hidden` (già in `styles.css`, non nuovo), mai `hidden` né
 * `display: none`, che lo toglierebbero anche dall'albero. Chi guarda preme
 * (i) per vederlo; chi usa uno screen reader lo sente comunque, sempre, allo
 * stesso modo di prima — il pulsante è un aiuto in più per chi vede, non un
 * cancello per chi non vede.
 *
 * `aria-pressed`, non `aria-expanded`: quest'ultimo dichiara che il
 * contenuto compare o sparisce dall'albero di accessibilità al clic, che qui
 * non succede mai — il contenuto c'è sempre. `aria-pressed` dice solo lo
 * stato del pulsante, senza promettere qualcosa che il componente non fa.
 *
 * L'id lo sceglie chi chiama, non un id generato: deve combaciare con l'
 * `aria-describedby` scritto a mano sul campo — stesso schema di
 * `new-slug-hint` e affini, solo dietro un pulsante invece che sempre in
 * vista.
 *
 * `label` è il nome del campo (es. "Sottodominio dell'ente"), usato per dare
 * a ogni pulsante un nome accessibile diverso. Cinque campi sulla stessa
 * schermata ("Nuovo ente") avevano tutti lo stesso `aria-label` ("Maggiori
 * informazioni"): chi scorre l'elenco dei controlli di uno screen reader li
 * vedeva tutti uguali, senza sapere quale campo riguardasse ciascuno fuori
 * dall'ordine di lettura (`accessibility`, in revisione).
 *
 * Nessun elemento avvolgente: il pulsante e il testo sono due nodi separati
 * (un frammento, non uno `<span>`), perché il chiamante li mette entrambi
 * dentro `.field__label-row` — il testo, quando visibile, deve potersi
 * disporre su una riga propria sotto etichetta e pulsante (`flex-wrap` +
 * `flex-basis: 100%` in `styles.css`) invece di allargare quella riga verso
 * destra.
 */
export function FieldHint({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="field-hint__toggle"
        aria-pressed={open}
        aria-label={t('platform.more_info_about', { field: label })}
        onClick={() => setOpen((current) => !current)}
      >
        <InfoIcon />
      </button>
      <p
        id={id}
        className={open ? 'muted field-hint__panel' : 'muted field-hint__panel visually-hidden'}
      >
        {children}
      </p>
    </>
  );
}
